import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Users, 
  Building2, 
  Search, 
  ArrowRight, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff,
  Loader2,
  ArrowLeft,
  User,
  Phone,
  FileCheck,
  Heart,
  Car
} from 'lucide-react';
import { useExchangeAuth } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

const roleOptions = [
  {
    id: 'patient',
    title: 'Patient / Rider',
    description: 'Upload insurance & book rides',
    icon: Heart,
    color: 'neon-cyan'
  },
  {
    id: 'driver',
    title: 'Driver',
    description: 'Manage your credentials & earn',
    icon: Car,
    color: 'neon-cyan'
  },
  {
    id: 'provider',
    title: 'Provider',
    description: 'Manage company & driver credentials',
    icon: Building2,
    color: 'neon-green'
  },
  {
    id: 'broker',
    title: 'Broker',
    description: 'Verify provider credentials',
    icon: Search,
    color: 'neon-purple'
  }
];

export const PortalLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useExchangeAuth();
  
  // Check for role in URL query params (from OVA Xchange splash page)
  const urlRole = searchParams.get('role');
  const validRoles = ['patient', 'driver', 'provider', 'broker'];
  const preselectedRole = validRoles.includes(urlRole) ? urlRole : null;
  
  const [mode, setMode] = useState(preselectedRole ? 'register' : 'login');
  const [selectedRole, setSelectedRole] = useState(preselectedRole);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    smsConsent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await login(formData.email, formData.password);
      toast.success('Welcome back!');
      
      // Navigate to appropriate portal based on role
      const role = result.role || 'driver';
      navigate(`/portal/${role}`);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await register({
        ...formData,
        role: selectedRole
      });
      toast.success('Account created! Please complete your profile.');
      navigate(`/portal/${selectedRole}`);
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-medex-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back link */}
        <Link
          to="/xchange"
          className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to OVA Xchange
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-neon-green/20 flex items-center justify-center mx-auto mb-4">
              <FileCheck className="w-8 h-8 text-neon-green" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-white mb-2">
              {mode === 'login' ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-slate-400 text-sm">
              {mode === 'login' 
                ? 'Sign in to access your OVA Xchange portal' 
                : 'Join the OVA Xchange'}
            </p>
          </div>

          {/* Role Selection (for registration) */}
          {mode === 'select-role' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-300 text-center mb-4">What type of account do you need?</p>
              {roleOptions.map((role) => {
                const RoleIcon = role.icon;
                return (
                  <button
                    key={role.id}
                    onClick={() => {
                      setSelectedRole(role.id);
                      setMode('register');
                    }}
                    className={`w-full p-4 rounded-lg border transition-all text-left flex items-center space-x-4 ${
                      selectedRole === role.id
                        ? `border-${role.color} bg-${role.color}/10`
                        : 'border-white/10 bg-white/5 hover:border-white/20'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl bg-${role.color}/20 flex items-center justify-center`}>
                      <RoleIcon className={`w-6 h-6 text-${role.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-medium">{role.title}</div>
                      <div className="text-sm text-slate-400">{role.description}</div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </button>
                );
              })}
              
              <button
                onClick={() => setMode('login')}
                className="w-full text-center text-sm text-slate-400 hover:text-white mt-4"
              >
                Already have an account? Sign in
              </button>
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full input-dark pl-10"
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full input-dark pl-10 pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setMode('select-role')}
                  className="text-sm text-slate-400 hover:text-neon-green"
                >
                  Don't have an account? Create one
                </button>
              </div>
            </form>
          )}

          {/* Register Form */}
          {mode === 'register' && selectedRole && (
            <form onSubmit={handleRegister} className="space-y-4">
              {/* Selected role indicator */}
              <div className="flex items-center space-x-3 p-3 rounded-lg bg-white/5 mb-4">
                {(() => {
                  const role = roleOptions.find(r => r.id === selectedRole);
                  const RoleIcon = role?.icon || Users;
                  return (
                    <>
                      <div className={`w-10 h-10 rounded-lg bg-${role?.color}/20 flex items-center justify-center`}>
                        <RoleIcon className={`w-5 h-5 text-${role?.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="text-white text-sm font-medium">{role?.title} Account</div>
                        <button
                          type="button"
                          onClick={() => setMode('select-role')}
                          className="text-xs text-slate-400 hover:text-white"
                        >
                          Change role
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className="w-full input-dark pl-10"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full input-dark"
                    placeholder="Smith"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full input-dark pl-10"
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone (Optional)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full input-dark pl-10"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="w-full input-dark pl-10 pr-10"
                    placeholder="Min 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* SMS Consent Checkbox - SEPARATE FROM OTHER OPT-INS */}
              <div className="p-4 bg-slate-800/80 rounded-lg border-2 border-neon-cyan/30">
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2 text-neon-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  SMS Text Message Consent
                </h4>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="smsConsent"
                    checked={formData.smsConsent || false}
                    onChange={handleChange}
                    className="mt-1 w-5 h-5 rounded border-2 border-neon-cyan bg-slate-700 text-neon-green focus:ring-neon-green focus:ring-offset-0"
                    data-testid="sms-consent-checkbox"
                  />
                  <div className="text-xs text-slate-300 leading-relaxed">
                    <p className="mb-2">
                      <strong className="text-white">I agree to receive SMS text messages from MedexOVA</strong> at the phone number provided above regarding ride notifications, account alerts, and service updates.
                    </p>
                    <p className="text-slate-400">
                      Message frequency may vary. Standard message and data rates may apply. Reply <strong className="text-neon-cyan">STOP</strong> to opt out. Reply <strong className="text-neon-cyan">HELP</strong> for assistance. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.
                    </p>
                    <p className="mt-2 text-slate-500">
                      View our <a href="/privacy-policy#sms-consent" target="_blank" className="text-neon-cyan hover:underline">SMS Policy</a> for more details.
                    </p>
                  </div>
                </label>
              </div>

              {/* Terms Agreement - SEPARATE */}
              <div className="text-xs text-slate-500 text-center">
                By creating an account, you agree to our{' '}
                <a href="/terms-of-service" target="_blank" className="text-neon-cyan hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy-policy" target="_blank" className="text-neon-cyan hover:underline">Privacy Policy</a>.
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>Create Account</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-sm text-slate-400 hover:text-neon-green"
                >
                  Already have an account? Sign in
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PortalLogin;
